import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState, useRef } from "react";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import { rowMatchesUnifiedSearch } from "../../shared/unifiedSearch";
import { Button } from "../../components/ui/button";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { Option } from "../../interface/Option";
import { ActivityNotAllowedAxiosInstance } from "../../Network/ActivityNotAllowedAxiosInstance";
import { activityNotAllowed, Datum as DatumBase } from "../../interface/ActivityNotAllowed";
import ListofActivityCode from "../Shared/ListOfActivityCode";
import ActivitySelector from "../Shared/ActivityType";
import ListOfActivityCustom from "../Shared/ListOfActivityCustom";
import ListOfActivityMedical from "../Shared/ListOfActivityMedical";
import ListOfNewNetworkName from "../Shared/NewNetworkName";
import ListOfInsuranceplanName from "../Shared/ListOfInsuranceplanName";
import VipPatientSelector from "Pages/Shared/VipPatient";
import ListOfProviderName from "../Shared/ListOfProviderName";
import PolicyNames from "../Shared/PolicyNames";
import FileUpload from "Pages/uploadFile/ActivityNotAllowed/ActivityNotAllowed";
import ListOfDoctorName from "../Shared/Doctor";
import Vip from "../Shared/Vip";
import ListOfSpecialty from "../Shared/Specialty";

// Extend Datum with the new fields returned by the backend
type Datum = DatumBase & {
  types_main?: any[];
  types_not_allowed?: any[];
};

export interface PolicyItem {
  id?: number;
  Policy_id: string;
  Policy_name: string;
  plans?: Array<{
    Plan_name?: string;
    plan_name?: string;
    name?: string;
    id?: number;
    [key: string]: any;
  }>;
}

// ─── Helper: color per activity type ──────────────────────────────────────────
const getActivityTypeColor = (type: string) => {
  const colors: Record<string, { bg: string; border: string; text: string }> = {
    CPT:    { bg: "bg-blue-100",   border: "border-blue-500",   text: "text-blue-900"   },
    HCPCS:  { bg: "bg-green-100",  border: "border-green-500",  text: "text-green-900"  },
    Dental: { bg: "bg-purple-100", border: "border-purple-500", text: "text-purple-900" },
    Drugs:  { bg: "bg-orange-100", border: "border-orange-500", text: "text-orange-900" },
  };
  return colors[type] || { bg: "bg-gray-100", border: "border-gray-400", text: "text-gray-900" };
};

// ─── Normalise raw type string → display key ──────────────────────────────────
const normaliseType = (raw: string): string => {
  const map: Record<string, string> = { cpt: "CPT", hcpcs: "HCPCS", dental: "Dental", drugs: "Drugs" };
  return map[raw?.toLowerCase()] || raw;
};

const normalizeActivityItem = (item: any): any => {
  const tm = item.types_main || [];
  const isObjMain = tm.length > 0 && typeof tm[0] === "object" && tm[0] !== null && "code" in tm[0];

  let types_main_normalized: Array<{code: string; type: string}> = [];
  if (isObjMain) {
    types_main_normalized = tm.map((t: any) => ({
      code: t.code?.toString() ?? "",
      type: normaliseType(t.type ?? "CPT"),
    })).filter((x: any) => x.code);
  } else if (item.main_activity?.length && item.activity_type) {
    const types = Array.isArray(item.activity_type)
      ? item.activity_type
      : Array(item.main_activity.length).fill(item.activity_type);
    types_main_normalized = item.main_activity.map((code: string, i: number) => ({
      code,
      type: normaliseType(types[i] || "CPT"),
    }));
  }

  const tna = item.types_not_allowed || [];
  const isObjNotAllowed = tna.length > 0 && typeof tna[0] === "object" && tna[0] !== null && "code" in tna[0];

  let types_not_allowed_normalized: Array<{code: string; type: string}> = [];
  if (isObjNotAllowed) {
    types_not_allowed_normalized = tna.map((t: any) => ({
      code: t.code?.toString() ?? "",
      type: normaliseType(t.type ?? "CPT"),
    })).filter((x: any) => x.code);
  } else if (item.not_allowed_activity?.length && item.activity_types) {
    const types = Array.isArray(item.activity_types)
      ? item.activity_types
      : Array(item.not_allowed_activity.length).fill(item.activity_types);
    types_not_allowed_normalized = item.not_allowed_activity.map((code: string, i: number) => ({
      code,
      type: normaliseType(types[i] || "CPT"),
    }));
  }

  return {
    ...item,
    types_main: types_main_normalized,
    types_not_allowed: types_not_allowed_normalized,
  };
};

// ─── preserveScrollCheckbox helper ───────────────────────────────────────────
const preserveScrollCheckbox = (e: React.ChangeEvent<HTMLInputElement>, fn: () => void) => {
  const el = e.currentTarget as HTMLElement;
  const dialog = el.closest('[role="dialog"]');
  const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
  const scrollTop = scrollable?.scrollTop ?? 0;
  fn();
  requestAnimationFrame(() => {
    if (scrollable) scrollable.scrollTop = scrollTop;
  });
};

// ─── getUserRole ─────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const SourcePicker: React.FC<{
  title: string;
  selected: string | null;
  onSelect: (key: string | null) => void;
  options: { key: string; label: string; icon: string; count: number }[];
  children: React.ReactNode;
}> = ({ title, selected, onSelect, options, children }) => (
  <div>
    <Label className="mb-2 block">{title}</Label>
    <div className="flex bg-slate-100 rounded-lg p-1 border border-slate-200 gap-1">
      {options.map((opt) => {
        const isSelected = selected === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelect(isSelected ? null : opt.key)}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2.5 text-sm font-medium transition-all ${isSelected ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            <i className={`fa-solid ${opt.icon} text-xs`}></i>
            <span>{opt.label}</span>
            {opt.count > 0 && (
              <span className={`text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center ${isSelected ? "bg-blue-100 text-blue-700" : "bg-slate-300 text-slate-600"}`}>
                {opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
    {selected && (
      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
        {children}
      </div>
    )}
  </div>
);


// ─── EntityCard Component ─────────────────────────────────────────────────────
const entityColors: Record<string, { border: string; bg: string; text: string; badgeBg: string }> = {
  blue:    { border: "border-blue-200",    bg: "bg-blue-50",    text: "text-blue-700",    badgeBg: "bg-blue-50 text-blue-700 border-blue-200"    },
  cyan:    { border: "border-cyan-200",    bg: "bg-cyan-50",    text: "text-cyan-700",    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-200"    },
  purple:  { border: "border-purple-200",  bg: "bg-purple-50",  text: "text-purple-700",  badgeBg: "bg-purple-50 text-purple-700 border-purple-200"  },
  amber:   { border: "border-amber-200",   bg: "bg-amber-50",   text: "text-amber-700",   badgeBg: "bg-amber-50 text-amber-700 border-amber-200"   },
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pink:    { border: "border-pink-200",    bg: "bg-pink-50",    text: "text-pink-700",    badgeBg: "bg-pink-50 text-pink-700 border-pink-200"    },
  yellow:  { border: "border-yellow-200",  bg: "bg-yellow-50",  text: "text-yellow-700",  badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-200"  },
  indigo:  { border: "border-indigo-200",  bg: "bg-indigo-50",  text: "text-indigo-700",  badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200"  },
  slate:   { border: "border-slate-200",   bg: "bg-slate-50",   text: "text-slate-700",   badgeBg: "bg-slate-50 text-slate-700 border-slate-200"   },
  teal:    { border: "border-teal-200",    bg: "bg-teal-50",    text: "text-teal-700",    badgeBg: "bg-teal-50 text-teal-700 border-teal-200"    },
};

interface EntityCardProps {
  label: string;
  icon: string;
  color: string;
  isActive: boolean;
  mode: "EXCLUSION" | "INCLUSION" | null;
  onExclusion: () => void;
  onInclusion: () => void;
  onClear: () => void;
  children: React.ReactNode;
}

const EntityCard: React.FC<EntityCardProps> = ({
  label, icon, color, isActive, mode, onExclusion, onInclusion, onClear, children,
}) => {
  const c = entityColors[color] ?? entityColors.slate;

  const preserveScroll = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dialog = (e.currentTarget as HTMLElement).closest('[role="dialog"]');
    const scrollable = dialog?.querySelector('.overflow-y-auto') as HTMLElement | null;
    const scrollTop = scrollable?.scrollTop ?? 0;
    fn();
    requestAnimationFrame(() => {
      if (scrollable) scrollable.scrollTop = scrollTop;
    });
  };

  return (
    <div className={`bg-white rounded-xl border p-4 transition-all relative ${isActive ? `${c.border} shadow-sm` : "border-slate-100"}`}>
      {isActive && (
        <button
          type="button"
          onClick={preserveScroll(onClear)}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-slate-50 text-slate-400 hover:bg-red-50 hover:text-red-500 transition-all border border-slate-100 shadow-sm z-10"
          title="Clear"
        >
          <i className="fa-solid fa-xmark text-[14px]"></i>
        </button>
      )}
      <div className="flex flex-col items-center gap-4">
        <span className={`px-4 py-1 rounded-full text-[10px] font-black tracking-tighter border flex items-center gap-2 ${isActive ? c.badgeBg : "bg-slate-50 text-slate-400 border-slate-200"}`}>
          <i className={`fa-solid ${icon} text-[12px]`}></i> {label}
        </span>
        <div className="flex bg-slate-100/80 rounded-lg p-1 border border-slate-200 w-full shadow-inner">
          <button
            type="button"
            onClick={preserveScroll(onExclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "EXCLUSION" ? "bg-red-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Exclude
          </button>
          <button
            type="button"
            onClick={preserveScroll(onInclusion)}
            className={`flex-1 py-2 text-[11px] font-bold rounded-md transition-all ${isActive && mode === "INCLUSION" ? "bg-emerald-600 text-white shadow-md" : "text-slate-500 hover:bg-slate-200"}`}
          >
            Include
          </button>
        </div>
      </div>
      {isActive && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

// ─── mode conversion helpers ──────────────────────────────────────────────────
const modeToCard = (mode: string | undefined): { cardMode: "EXCLUSION" | "INCLUSION" | null; isActive: boolean } => {
  if (mode === "EXCLUDED") return { cardMode: "EXCLUSION", isActive: true };
  if (mode === "INCLUDED") return { cardMode: "INCLUSION", isActive: true };
  return { cardMode: null, isActive: false };
};

const cardToMode = (cardMode: "EXCLUSION" | "INCLUSION" | null): string => {
  if (cardMode === "EXCLUSION") return "EXCLUDED";
  if (cardMode === "INCLUSION") return "INCLUDED";
  return "DISABLED";
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ActivityNotAllowed: React.FC = () => {
  const [items, setItems] = useState<activityNotAllowed>({ result: [] });

  // ── Basic ──
  const [relationName, setRelationName]   = useState<string>("");
  const [isActive,     setIsActive]       = useState<boolean>(true);
  const [editIndex,    setEditIndex]      = useState<null | number>(null);
  const [isDialogOpen, setIsDialogOpen]   = useState(false);

  // ── Activity One (per-type buckets) ──
  const [selectedMainOption,   setSelectedMainOption]   = useState<string | null>(null);
  const [mainActivitiesByType, setMainActivitiesByType] = useState<{ CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] }>({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
  const [mainActivityMedicalGroups, setMainActivityMedicalGroups] = useState<Option[]>([]);
  const [mainActivityCustomGroups,  setMainActivityCustomGroups]  = useState<Option[]>([]);

  // ── Activity Two (per-type buckets) ──
  const [selectedNotAllowedOption,   setSelectedNotAllowedOption]   = useState<string | null>(null);
  const [notAllowedActivitiesByType, setNotAllowedActivitiesByType] = useState<{ CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] }>({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
  const [NotallowedActivityMedicalGroups, setNotAllowedActivityMedicalGroups] = useState<Option[]>([]);
  const [NotallowedActivityCustomGroups,  setNotAllowedActivityCustomGroups]  = useState<Option[]>([]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  // ── Entities ──
  const [provider,           setProvider]           = useState<Option[]>([]);
  const [providerMode,       setProviderMode]       = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isProviderActive,   setIsProviderActive]   = useState(false);
  const [selectAllProviders, setSelectAllProviders] = useState(false);

  const [networks,           setNetworks]           = useState<Option[]>([]);
  const [networkMode,        setNetworkMode]        = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isNetworkActive,    setIsNetworkActive]    = useState(false);
  const [selectAllNetworks,  setSelectAllNetworks]  = useState(false);

  const [insuranceplan,      setInsuranceplan]      = useState<Option[]>([]);
  const [insuranceMode,      setInsuranceMode]      = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isInsuranceActive,  setIsInsuranceActive]  = useState(false);
  const [selectAllInsurance, setSelectAllInsurance] = useState(false);

  const [Payer,              setPayer]              = useState<string[]>([]);
  const [payerMode,          setPayerMode]          = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPayerActive,      setIsPayerActive]      = useState(false);
  const [selectAllPayer,     setSelectAllPayer]     = useState(false);

  const [patients,           setPatients]           = useState<Option[]>([]);
  const [patientMode,        setPatientMode]        = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPatientActive,    setIsPatientActive]    = useState(false);
  const [selectAllPatients,  setSelectAllPatients]  = useState(false);

  const [policy,             setPolicy]             = useState<PolicyItem[]>([]);
  const [policyMode,         setPolicyMode]         = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isPolicyActive,     setIsPolicyActive]     = useState(false);
  const [selectAllPolicy,    setSelectAllPolicy]    = useState(false);

  const [userLevel,          setUserLevel]          = useState<string[]>([]);
  const [userMode,           setUserMode]           = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isUserActive,       setIsUserActive]       = useState(false);
  const [selectAllUser,      setSelectAllUser]      = useState(false);

  const [vip,                setVip]                = useState<Option[]>([]);
  const [vipMode,            setVipMode]            = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isVipActive,        setIsVipActive]        = useState(false);
  const [selectAllVip,       setSelectAllVip]       = useState(false);

  const [doctor,             setDoctor]             = useState<Option[]>([]);
  const [doctorMode,         setDoctorMode]         = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isDoctorActive,     setIsDoctorActive]     = useState(false);
  const [selectAllDoctor,    setSelectAllDoctor]    = useState(false);

  const [specialty,          setSpecialty]          = useState<Option[]>([]);
  const [specialtyMode,      setSpecialtyMode]      = useState<"EXCLUSION" | "INCLUSION" | null>(null);
  const [isSpecialtyActive,  setIsSpecialtyActive]  = useState(false);
  const [selectAllSpecialty, setSelectAllSpecialty] = useState(false);

  // ── Entities toggle ──
  const [showEntities, setShowEntities] = useState(false);

  // ── clear flags ──
  const [clearMainActivityMedicalGroups,       setClearMainActivityMedicalGroups]       = useState(false);
  const [clearMainActivityCustomGroups,        setClearMainActivityCustomGroups]        = useState(false);
  const [clearNotAllowedActivityMedicalGroups, setClearNotAllowedActivityMedicalGroups] = useState(false);
  const [clearNotAllowedActivityCustomGroups,  setClearNotAllowedActivityCustomGroups]  = useState(false);
  const [clearNetworks,      setClearNetworks]      = useState(false);
  const [clearInsuranceplan, setClearInsuranceplan] = useState(false);
  const [clearProvider,      setClearProvider]      = useState(false);
  const [clearPatient,       setClearPatient]       = useState(false);
  const [clearPolicy,        setClearPolicy]        = useState(false);
  const [clearDoctor,        setClearDoctor]        = useState(false);
  const [clearVip,           setClearVip]           = useState(false);
  const [clearSpecialty,     setClearSpecialty]     = useState(false);

  // ── Filters ──
  const [filterStatus,       setFilterStatus]       = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");
  // ── View dialog ──
  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  // ── "All codes" popup ──
  const [allCodesDialogOpen,  setAllCodesDialogOpen]  = useState(false);
  const [allCodesDialogTitle, setAllCodesDialogTitle] = useState("");
  const [allCodesDialogList,  setAllCodesDialogList]  = useState<Array<{ code: string; type: string }>>([]);

  const getToken = () => localStorage.getItem("token") || "";
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const { toast } = useToast();

  // ── Role & Admin Actions ──
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ── Force Dialog state ──
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");
  const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // validateEntities
  // ─────────────────────────────────────────────────────────────────────────────
  const validateEntities = (): string | null => {
    const checks = [
      { isActive: isProviderActive,  hasData: selectAllProviders  || provider.length > 0,     label: "Providers"      },
      { isActive: isDoctorActive,    hasData: selectAllDoctor     || doctor.length > 0,        label: "Doctor"         },
      { isActive: isNetworkActive,   hasData: selectAllNetworks   || networks.length > 0,      label: "Networks"       },
      { isActive: isInsuranceActive, hasData: selectAllInsurance  || insuranceplan.length > 0, label: "Insurance Plan" },
      { isActive: isPolicyActive,    hasData: selectAllPolicy     || policy.length > 0,        label: "Policy"         },
      { isActive: isPatientActive,   hasData: selectAllPatients   || patients.length > 0,      label: "Patients"       },
      { isActive: isVipActive,       hasData: selectAllVip        || vip.length > 0,           label: "VIP"            },
      { isActive: isPayerActive,     hasData: selectAllPayer      || Payer.length > 0,         label: "Payer"          },
      { isActive: isUserActive,      hasData: selectAllUser       || userLevel.length > 0,     label: "User Level"     },
      { isActive: isSpecialtyActive, hasData: selectAllSpecialty  || specialty.length > 0,     label: "Specialty"      },
    ];
    const empty = checks.filter(c => c.isActive && !c.hasData).map(c => c.label);
    if (empty.length >= 1) {
      return `The following cards are open but have no data selected: ${empty.join(", ")}. Please select at least one item for each, or close them using the ✕ button.`;
    }
    return null;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────────
  const flattenByType = (byType: { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] }) => {
    const out: Array<{ code: string | number; type: string }> = [];
    (Object.entries(byType) as [string, Option[]][]).forEach(([type, opts]) => {
      opts.forEach(o => out.push({ code: o.value, type }));
    });
    return out;
  };

  const buildBucketFromArrays = (codes: string[], types: string[]) => {
    const grouped: { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] } = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
    codes.forEach((code, i) => {
      const typeKey = normaliseType(types[i] || "CPT") as keyof typeof grouped;
      if (grouped[typeKey]) grouped[typeKey].push({ value: code, label: code.toString() });
    });
    return grouped;
  };

  const buildBucketFromTypesArray = (
    typesArr: any[],
    codesArr: string[],
  ): { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] } => {
    const grouped: { CPT: Option[]; HCPCS: Option[]; Dental: Option[]; Drugs: Option[] } = {
      CPT: [], HCPCS: [], Dental: [], Drugs: [],
    };
    const isObjectFormat =
      typesArr.length > 0 &&
      typeof typesArr[0] === "object" &&
      typesArr[0] !== null &&
      "code" in typesArr[0];

    if (isObjectFormat) {
      typesArr.forEach((t: any) => {
        const typeStr = t.type ?? "CPT";
        const codeStr = t.code?.toString() ?? "";
        const key = normaliseType(typeStr) as keyof typeof grouped;
        if (grouped[key] !== undefined && codeStr) grouped[key].push({ value: codeStr, label: codeStr });
      });
    } else {
      typesArr.forEach((t: any, i: number) => {
        const typeStr = typeof t === "string" ? t : "CPT";
        const codeStr = codesArr[i]?.toString() ?? "";
        const key = normaliseType(typeStr) as keyof typeof grouped;
        if (grouped[key] !== undefined && codeStr) grouped[key].push({ value: codeStr, label: codeStr });
      });
    }
    return grouped;
  };

  const buildProviderData = (providerList: any[]) =>
    providerList.map((o: any) => {
      if (o.providerId && o.providerName) return { Provider_id: o.providerId, Provider_name: o.providerName };
      if (o.Provider_id && o.Provider_name) return { Provider_id: o.Provider_id, Provider_name: o.Provider_name };
      const match = o.label?.match(/^\((.+)\)\s(.+)$/);
      if (match?.[1] && match?.[2]) return { Provider_id: match[1].trim(), Provider_name: match[2].trim() };
      return { Provider_id: o.value?.toString() || "", Provider_name: o.label?.toString() || "" };
    });

  const buildDoctorData = (doctorList: any[]) =>
    doctorList.map((o: any) => {
      if (o.Doctor_id !== undefined && o.Doctor_name !== undefined)
        return { Doctor_id: o.Doctor_id, Doctor_name: o.Doctor_name };
      const match = o.label?.match(/^\((.+)\)\s(.+)$/);
      if (match?.[1] && match?.[2]) return { Doctor_id: match[1].trim(), Doctor_name: match[2].trim() };
      return { Doctor_id: o.value?.toString() || "", Doctor_name: o.label?.toString() || "" };
    });

  const buildPolicyData = (policyList: any[]) =>
    policyList.map((p: any) => {
      if (typeof p === "object" && p.Policy_id && !p.value) return { id: p.id || 0, Policy_id: p.Policy_id, Policy_name: p.Policy_name, plans: p.plans || [] };
      if (typeof p === "object" && p.value) return { id: p.value.id || 0, Policy_id: p.value.Policy_id || "", Policy_name: p.value.Policy_name || "", plans: p.value.plans || [] };
      if (typeof p === "string") return { id: 0, Policy_id: p, Policy_name: p, plans: [] };
      return { id: 0, Policy_id: "", Policy_name: "", plans: [] };
    });

  // ─────────────────────────────────────────────────────────────────────────────
  // Reset all entity states
  // ─────────────────────────────────────────────────────────────────────────────
  const resetAllEntityStates = () => {
    setProvider([]);      setProviderMode(null);  setIsProviderActive(false);  setSelectAllProviders(false);
    setNetworks([]);      setNetworkMode(null);   setIsNetworkActive(false);   setSelectAllNetworks(false);
    setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setSelectAllInsurance(false);
    setPayer([]);         setPayerMode(null);     setIsPayerActive(false);     setSelectAllPayer(false);
    setPatients([]);      setPatientMode(null);   setIsPatientActive(false);   setSelectAllPatients(false);
    setPolicy([]);        setPolicyMode(null);    setIsPolicyActive(false);    setSelectAllPolicy(false);
    setUserLevel([]);     setUserMode(null);      setIsUserActive(false);      setSelectAllUser(false);
    setVip([]);           setVipMode(null);       setIsVipActive(false);       setSelectAllVip(false);
    setDoctor([]);        setDoctorMode(null);    setIsDoctorActive(false);    setSelectAllDoctor(false);
    setSpecialty([]);     setSpecialtyMode(null); setIsSpecialtyActive(false); setSelectAllSpecialty(false);
    setClearProvider(true);
    setClearNetworks(true);
    setClearInsuranceplan(true);
    setClearPatient(true);
    setClearPolicy(true);
    setClearDoctor(true);
    setClearVip(true);
    setClearSpecialty(true);
  };

  const handleToggleEntities = () => {
    if (showEntities) resetAllEntityStates();
    setShowEntities((prev) => !prev);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Filters
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // CRUD
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => { fetchUsers(); }, []);


  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Datum, { search: string; status: string; deleted: ServerDeletedFilterValue }>({
    filters: { search: filterSearch, status: filterStatus, deleted: deletedFilter },
    fetchPage: ({ page, limit, filters }) =>
      ActivityNotAllowedAxiosInstance.get(
        "getActivityNotAllowed",
        {
          params: {
            page,
            limit,
            is_active: filters.status === "true" || filters.status === "false" ? filters.status : undefined,
            is_deleted: filters.deleted,
          },
        }
      ),
    setRows: (rows) => setItems({ result: rows }),
    normalizeRow: normalizeActivityItem,
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1
        ),
    isClientFallbackActive: (filters) => filters.search.trim().length >= 2,
    clientFilterRows: (rows, filters) =>
      rows.filter((item) => rowMatchesUnifiedSearch(item, filters.search)),
    onError: (error) => {
      console.error("Failed to fetch Activities Not Allowed Together", error);
      toast({
        title: "Error",
        description: "Failed to fetch Activities Not Allowed Together",
        variant: "destructive",
      });
    },
  });

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Soft Delete
  // ─────────────────────────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotAllowedAxiosInstance.delete(
        `softDeleteActivityNotAllowed/${id}`, { data: { token: getToken() } }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Not Allowed deleted successfully." });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(response.data.error || "This relation is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete Activity Not Allowed";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Restore
  // ─────────────────────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotAllowedAxiosInstance.patch(
        `restoreActivityNotAllowed/${id}`, { token: getToken() }
      );
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Activity Not Allowed restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Hard Delete (permanent)
  // ─────────────────────────────────────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force
        ? `deleteActivityNotAllowed/${id}?force=true`
        : `deleteActivityNotAllowed/${id}`;
      const response = await ActivityNotAllowedAxiosInstance.delete(url, { data: { token: getToken() } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity Not Allowed permanently deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This relation is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Force Soft Delete
  // ─────────────────────────────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await ActivityNotAllowedAxiosInstance.delete(
        `softDeleteActivityNotAllowed/${id}?force=true`, { data: { token: getToken() } }
      );
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Activity Not Allowed force deleted successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  const clear = () => {
    setRelationName("");
    setSelectedMainOption(null);
    setMainActivitiesByType({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
    setMainActivityMedicalGroups([]);
    setMainActivityCustomGroups([]);
    setSelectedNotAllowedOption(null);
    setNotAllowedActivitiesByType({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
    setNotAllowedActivityMedicalGroups([]);
    setNotAllowedActivityCustomGroups([]);
    setIsActive(true);
    setShowEntities(false);
    resetAllEntityStates();
  };

  const handleOpenDialog = (item: Datum) => {
    setSelectedItem(normalizeActivityItem(item));
    setViewDialogOpen(true);
  };
const handleCloseDialog = () => { setViewDialogOpen(false); setSelectedItem(null); setShowAllUpdates(false); };

  const handleAddClick  = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };
  const handleEditClick = (item: Datum) => {
    const scrollY = window.scrollY;
    handleEdit(item);
    setIsDialogOpen(true);
    setTimeout(() => window.scrollTo({ top: scrollY, behavior: "instant" as ScrollBehavior }), 0);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // addItem
  // ─────────────────────────────────────────────────────────────────────────────
  const addItem = async (): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }

    const mainFlat       = flattenByType(mainActivitiesByType);
    const notAllowedFlat = flattenByType(notAllowedActivitiesByType);

    try {
      const newGroupArray = [{
        relation_name: relationName,
        activity_type:  mainFlat.map(a => a.type),
        main_activity:  mainFlat.map(a => a.code),
        types_main:     mainFlat.map(a => ({ code: a.code, type: a.type })),
        main_activity_medicalgroups: mainActivityMedicalGroups.map(o => o.value),
        main_activity_customgroups:  mainActivityCustomGroups.map(o => o.value),
        activity_types:         notAllowedFlat.map(a => a.type),
        not_allowed_activity:   notAllowedFlat.map(a => a.code),
        types_not_allowed:      notAllowedFlat.map(a => ({ code: a.code, type: a.type })),
        not_allowed_activity_medicalgroups: NotallowedActivityMedicalGroups.map(o => o.value),
        not_allowed_activity_customgroups:  NotallowedActivityCustomGroups.map(o => o.value),
        provider:           buildProviderData(provider),
        provider_mode:      cardToMode(providerMode),
        provider_all:       selectAllProviders,
        networks:           networks.map(o => o.value),
        network_mode:       cardToMode(networkMode),
        network_all:        selectAllNetworks,
        insuranceplan:      insuranceplan.map(o => Number(o.value)),
        insuranceplan_mode: cardToMode(insuranceMode),
        insuranceplan_all:  selectAllInsurance,
        payer:              Payer,
        payer_mode:         cardToMode(payerMode),
        payer_all:          selectAllPayer,
        patients:           patients.map(o => o.label),
        patient_mode:       cardToMode(patientMode),
        patient_all:        selectAllPatients,
        policy:             buildPolicyData(policy),
        policy_mode:        cardToMode(policyMode),
        policy_all:         selectAllPolicy,
        user_level:         userLevel,
        user_mode:          cardToMode(userMode),
        user_all:           selectAllUser,
        vip:                vip.map(o => o.value),
        vip_mode:           cardToMode(vipMode),
        vip_all:            selectAllVip,
        doctor:             buildDoctorData(doctor),
        doctor_mode:        cardToMode(doctorMode),
        doctor_all:         selectAllDoctor,
        specialty:          specialty.map(o => o.value),
        specialty_mode:     cardToMode(specialtyMode),
        specialty_all:      selectAllSpecialty,
        is_active:          isActive,
      }];

      const response = await ActivityNotAllowedAxiosInstance.post("addNewActivityNotAllowed", { data: newGroupArray, token: getToken() });
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New Activities Not Allowed Together added successfully." });
      } else {
        const errors = response.data.errors || [];
        toast({ title: "Error", description: errors.map((e: any) => e.error ?? e.message ?? "Unknown error").join("\n") || response.data.message || "Failed to add.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // updateItem
  // ─────────────────────────────────────────────────────────────────────────────
  const updateItem = async (id: string | number): Promise<void> => {
    if (!relationName.trim()) { toast({ title: "Error", description: "Relation Name is required.", variant: "destructive" }); return; }
    if (relationName.trim().length < 3) { toast({ title: "Error", description: "Relation Name must be at least 3 characters.", variant: "destructive" }); return; }
    const entityError = validateEntities();
    if (entityError) { toast({ title: "Validation Error", description: entityError, variant: "destructive" }); return; }

    const mainFlat       = flattenByType(mainActivitiesByType);
    const notAllowedFlat = flattenByType(notAllowedActivitiesByType);

    try {
      const updatedItem = {
        data: {
          relation_name:  relationName,
          is_active:      isActive,
          activity_type:  mainFlat.map(a => a.type),
          main_activity:  mainFlat.map(a => a.code),
          types_main:     mainFlat.map(a => ({ code: a.code, type: a.type })),
          main_activity_medicalgroups: mainActivityMedicalGroups.map(o => o.value),
          main_activity_customgroups:  mainActivityCustomGroups.map(o => o.value),
          activity_types:        notAllowedFlat.map(a => a.type),
          not_allowed_activity:  notAllowedFlat.map(a => a.code),
          types_not_allowed:     notAllowedFlat.map(a => ({ code: a.code, type: a.type })),
          not_allowed_activity_medicalgroups: NotallowedActivityMedicalGroups.map(o => o.value),
          not_allowed_activity_customgroups:  NotallowedActivityCustomGroups.map(o => o.value),
          provider:           buildProviderData(provider),
          provider_mode:      cardToMode(providerMode),
          provider_all:       selectAllProviders,
          networks:           networks.map(o => o.value),
          network_mode:       cardToMode(networkMode),
          network_all:        selectAllNetworks,
          insuranceplan:      insuranceplan.map(o => Number(o.value)),
          insuranceplan_mode: cardToMode(insuranceMode),
          insuranceplan_all:  selectAllInsurance,
          payer:              Payer,
          payer_mode:         cardToMode(payerMode),
          payer_all:          selectAllPayer,
          patients:           patients.map(o => o.label),
          patient_mode:       cardToMode(patientMode),
          patient_all:        selectAllPatients,
          policy:             buildPolicyData(policy),
          policy_mode:        cardToMode(policyMode),
          policy_all:         selectAllPolicy,
          user_level:         userLevel,
          user_mode:          cardToMode(userMode),
          user_all:           selectAllUser,
          vip:                vip.map(o => o.value),
          vip_mode:           cardToMode(vipMode),
          vip_all:            selectAllVip,
          doctor:             buildDoctorData(doctor),
          doctor_mode:        cardToMode(doctorMode),
          doctor_all:         selectAllDoctor,
          specialty:          specialty.map(o => o.value),
          specialty_mode:     cardToMode(specialtyMode),
          specialty_all:      selectAllSpecialty,
          id,
        },
        token: getToken(),
      };
      const response = await ActivityNotAllowedAxiosInstance.put("updateActivityNotAllowed", updatedItem);
      if (response.data.success) {
        await fetchItems();
        clear();
        setIsDialogOpen(false);
        if (selectedItem && selectedItem.id === id) {
          setSelectedItem(null);
          dialogRef.current?.close();
        }
        toast({ title: "Success", description: "Activities Not Allowed Together updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // toggleActiveStatus — with force support
  // ─────────────────────────────────────────────────────────────────────────────
  const toggleActiveStatus = async (item: Datum, force = false) => {
    try {
      const mainFlat = (item.main_activity || []).map((code: string, i: number) => {
        const t = item.types_main?.[i];
        const type = typeof t === "object" && t !== null && "type" in t
          ? (t as any).type
          : typeof t === "string" ? t : "CPT";
        return { code, type };
      });
      const notAllowedFlat = (item.not_allowed_activity || []).map((code: string, i: number) => {
        const t = item.types_not_allowed?.[i];
        const type = typeof t === "object" && t !== null && "type" in t
          ? (t as any).type
          : typeof t === "string" ? t : "CPT";
        return { code, type };
      });

      const url = force ? "updateActivityNotAllowed?force=true" : "updateActivityNotAllowed";
      const updatedItem = {
        data: {
          relation_name:  item.relation_name,
          is_active:      !item.is_active,
          activity_type:  mainFlat.map((a: any) => a.type),
          main_activity:  item.main_activity || [],
          types_main:     mainFlat.map((a: any) => ({ code: a.code, type: a.type })),
          main_activity_medicalgroups: (item.main_activity_medicalgroups || []).map((g: any) => g.id ?? g),
          main_activity_customgroups:  (item.main_activity_customgroups  || []).map((g: any) => g.id ?? g),
          activity_types:        notAllowedFlat.map((a: any) => a.type),
          not_allowed_activity:  item.not_allowed_activity || [],
          types_not_allowed:     notAllowedFlat.map((a: any) => ({ code: a.code, type: a.type })),
          not_allowed_activity_medicalgroups: (item.not_allowed_activity_medicalgroups || []).map((g: any) => g.id ?? g),
          not_allowed_activity_customgroups:  (item.not_allowed_activity_customgroups  || []).map((g: any) => g.id ?? g),
          provider: (item.provider || []).map((o: any) => ({ Provider_id: o.Provider_id || "", Provider_name: o.Provider_name || "" })),
          provider_mode: (item as any).provider_mode ?? "DISABLED",
          provider_all:  (item as any).provider_all  ?? false,
     networks: ((item.networks ?? (item as any).network) || []).map((n: any) => n.id ?? n),
          network_mode: (item as any).network_mode ?? "DISABLED",
          network_all:  (item as any).network_all  ?? false,
          insuranceplan: (item.insuranceplan || []).map((i: any) => i.id ?? i),
          insuranceplan_mode: (item as any).insuranceplan_mode ?? "DISABLED",
          insuranceplan_all:  (item as any).insuranceplan_all  ?? false,
          payer: item.payer || [],
          payer_mode: (item as any).payer_mode ?? "DISABLED",
          payer_all:  (item as any).payer_all  ?? false,
          patients: ((item.patients ?? (item as any).patient) || []).map((p: any) => p.name ?? p),
          patient_mode: (item as any).patient_mode ?? "DISABLED",
          patient_all:  (item as any).patient_all  ?? false,
          policy: (item.policy || []).map((p: any) => ({ id: p.id || 0, Policy_id: p.Policy_id || "", Policy_name: p.Policy_name || "", plans: p.plans || [] })),
          policy_mode: (item as any).policy_mode ?? "DISABLED",
          policy_all:  (item as any).policy_all  ?? false,
          user_level: item.user_level || [],
          user_mode: (item as any).user_mode ?? "DISABLED",
          user_all:  (item as any).user_all  ?? false,
          vip: (item.vip || []).map((v: any) => v.id ?? v),
          vip_mode: (item as any).vip_mode ?? "DISABLED",
          vip_all:  (item as any).vip_all  ?? false,
          doctor: (item.doctor || []).map((d: any) => ({ Doctor_id: d.Doctor_id ?? "", Doctor_name: d.Doctor_name ?? d })),
          doctor_mode: (item as any).doctor_mode ?? "DISABLED",
          doctor_all:  (item as any).doctor_all  ?? false,
          specialty: (item.specialty || []).map((s: any) => s.id ?? s),
          specialty_mode: (item as any).specialty_mode ?? "DISABLED",
          specialty_all:  (item as any).specialty_all  ?? false,
          id: item.id,
        },
        token: getToken(),
      };
      const response = await ActivityNotAllowedAxiosInstance.put(url, updatedItem);
      if (response.data.success) {
        setItems((prev) => {
          const newResult = [...prev.result];
          const idx = newResult.findIndex((i) => i.id === item.id);
          if (idx !== -1) newResult[idx] = { ...newResult[idx], is_active: !item.is_active };
          return { result: newResult };
        });
        setForceDialogOpen(false);
        setForceError("");
        toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
      } else {
        setForceId(item.id);
        setForceAction("status");
        setForceError(response.data.error || "This relation is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "An unexpected error occurred.";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(item.id);
        setForceAction("status");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // handleEdit
  // ─────────────────────────────────────────────────────────────────────────────
  const handleEdit = (item: Datum) => {
    setRelationName(item.relation_name);

    // ── Activity One ──
    const tm = item.types_main || [];
    const typesMainIsObjectFormat = tm.length > 0 && typeof tm[0] === "object" && tm[0] !== null && "code" in tm[0];
    if (typesMainIsObjectFormat) {
      setMainActivitiesByType(buildBucketFromTypesArray(tm, item.main_activity || []));
    } else if (item.main_activity?.length && item.activity_type) {
      const codes = item.main_activity as string[];
      const types = Array.isArray(item.activity_type) ? item.activity_type : Array(codes.length).fill(item.activity_type);
      setMainActivitiesByType(buildBucketFromArrays(codes, types));
    } else {
      setMainActivitiesByType({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
    }

    if (item.main_activity_medicalgroups?.length)
      setMainActivityMedicalGroups(item.main_activity_medicalgroups.map((o: any) => ({ value: o.id, label: o.name })));
    if (item.main_activity_customgroups?.length)
      setMainActivityCustomGroups(item.main_activity_customgroups.map((o: any) => ({ value: o.id, label: o.name })));

    if (typesMainIsObjectFormat || item.main_activity?.length) setSelectedMainOption("list_of_Activity_code");
    else if (item.main_activity_medicalgroups?.length) setSelectedMainOption("medical_Activity_groups");
    else if (item.main_activity_customgroups?.length)  setSelectedMainOption("custom_Activity_groups");
    else setSelectedMainOption(null);

    // ── Activity Two ──
    const tna = item.types_not_allowed || [];
    const typesNotAllowedIsObjectFormat = tna.length > 0 && typeof tna[0] === "object" && tna[0] !== null && "code" in tna[0];
    if (typesNotAllowedIsObjectFormat) {
      setNotAllowedActivitiesByType(buildBucketFromTypesArray(tna, item.not_allowed_activity || []));
    } else if (item.not_allowed_activity?.length && item.activity_types) {
      const codes = item.not_allowed_activity as string[];
      const types = Array.isArray(item.activity_types) ? item.activity_types : Array(codes.length).fill(item.activity_types);
      setNotAllowedActivitiesByType(buildBucketFromArrays(codes, types));
    } else {
      setNotAllowedActivitiesByType({ CPT: [], HCPCS: [], Dental: [], Drugs: [] });
    }

    if (item.not_allowed_activity_medicalgroups?.length)
      setNotAllowedActivityMedicalGroups(item.not_allowed_activity_medicalgroups.map((o: any) => ({ value: o.id, label: o.name })));
    if (item.not_allowed_activity_customgroups?.length)
      setNotAllowedActivityCustomGroups(item.not_allowed_activity_customgroups.map((o: any) => ({ value: o.id, label: o.name })));

    if (typesNotAllowedIsObjectFormat || item.not_allowed_activity?.length) setSelectedNotAllowedOption("list_of_Activity");
    else if (item.not_allowed_activity_medicalgroups?.length) setSelectedNotAllowedOption("medical_Activity_groups");
    else if (item.not_allowed_activity_customgroups?.length)  setSelectedNotAllowedOption("custom_Activity_groups");
    else setSelectedNotAllowedOption(null);

    // ── Entities ──
    const toObjArr = (arr: any[], stringKey: string) =>
      Array.isArray(arr) ? arr.map(o => typeof o === "string" ? { id: 0, [stringKey]: o } : o) : [];

    // Provider
    const mappedProviders = toObjArr(item.provider, "Provider_name").map((o: any) => ({
      value: o.Provider_id, label: o.Provider_id ? `(${o.Provider_id}) ${o.Provider_name}` : o.Provider_name,
    }));
    setProvider(mappedProviders);
    const pCard = modeToCard((item as any).provider_mode);
    setProviderMode(pCard.cardMode); setIsProviderActive(pCard.isActive || mappedProviders.length > 0);
    setSelectAllProviders((item as any).provider_all ?? false);

    // Networks
    const networksSource = item.networks ?? (item as any).network ?? [];
    const mappedNetworks = toObjArr(networksSource, "network_name").map((o: any) => ({
      value: o.id, label: o.id ? `(${o.id}) ${o.network_name}` : o.network_name,
    }));
    setNetworks(mappedNetworks);
    const nCard = modeToCard((item as any).network_mode);
    setNetworkMode(nCard.cardMode); setIsNetworkActive(nCard.isActive || mappedNetworks.length > 0);
    setSelectAllNetworks((item as any).network_all ?? false);

    // Insurance
    const mappedInsurance = toObjArr(item.insuranceplan, "Plan_name").map((o: any) => ({
      value: o.id, label: o.id ? `(${o.id}) ${o.Plan_name}` : o.Plan_name,
    }));
    setInsuranceplan(mappedInsurance);
    const iCard = modeToCard((item as any).insuranceplan_mode);
    setInsuranceMode(iCard.cardMode); setIsInsuranceActive(iCard.isActive || mappedInsurance.length > 0);
    setSelectAllInsurance((item as any).insuranceplan_all ?? false);

    // Patients
    const patientsSource = item.patients ?? (item as any).patient ?? [];
    const mappedPatients = toObjArr(patientsSource, "name").map((o: any) => ({
      value: o.id, label: o.id ? `(${o.id}) ${o.name}` : o.name,
    }));
    setPatients(mappedPatients);
    const patCard = modeToCard((item as any).patient_mode);
    setPatientMode(patCard.cardMode); setIsPatientActive(patCard.isActive || mappedPatients.length > 0);
    setSelectAllPatients((item as any).patient_all ?? false);

    // VIP
    const mappedVip = toObjArr(item.vip, "name").map((o: any) => ({
      value: o.id, label: o.id ? `(${o.id}) ${o.name}` : o.name,
    }));
    setVip(mappedVip);
    const vCard = modeToCard((item as any).vip_mode);
    setVipMode(vCard.cardMode); setIsVipActive(vCard.isActive || mappedVip.length > 0);
    setSelectAllVip((item as any).vip_all ?? false);

    // Doctor
    const mappedDoctors = toObjArr(item.doctor, "Doctor_name").map((o: any) => ({
      value: o.Doctor_id ?? o.id ?? "",
      label: o.Doctor_id ? `(${o.Doctor_id}) ${o.Doctor_name}` : (o.Doctor_name ?? String(o)),
      Doctor_id: o.Doctor_id ?? "",
      Doctor_name: o.Doctor_name ?? "",
    }));
    setDoctor(mappedDoctors);
    const dCard = modeToCard((item as any).doctor_mode);
    setDoctorMode(dCard.cardMode); setIsDoctorActive(dCard.isActive || mappedDoctors.length > 0);
    setSelectAllDoctor((item as any).doctor_all ?? false);

    // Policy
    const mappedPolicy = Array.isArray(item.policy)
      ? item.policy.map((obj: any) => ({ id: obj.id || 0, Policy_id: obj.Policy_id || "", Policy_name: obj.Policy_name || "", plans: obj.plans || [] }))
      : [];
    setPolicy(mappedPolicy);
    const polCard = modeToCard((item as any).policy_mode);
    setPolicyMode(polCard.cardMode); setIsPolicyActive(polCard.isActive || mappedPolicy.length > 0);
    setSelectAllPolicy((item as any).policy_all ?? false);

    // Payer
    setPayer(item.payer || []);
    const payCard = modeToCard((item as any).payer_mode);
    setPayerMode(payCard.cardMode); setIsPayerActive(payCard.isActive || (item.payer?.length > 0));
    setSelectAllPayer((item as any).payer_all ?? false);

    // User Level
    setUserLevel(item.user_level || []);
    const uCard = modeToCard((item as any).user_mode);
    setUserMode(uCard.cardMode); setIsUserActive(uCard.isActive || (item.user_level?.length > 0));
    setSelectAllUser((item as any).user_all ?? false);

    // Specialty
    const specialtyData = Array.isArray((item as any).specialty)
      ? (item as any).specialty.map((obj: any) => typeof obj === "string" ? { id: 0, name: obj } : obj)
      : [];
    const mappedSpecialty = specialtyData.map((obj: any) => ({
      value: obj.id,
      label: obj.id ? `(${obj.id}) ${obj.name}` : obj.name,
    }));
    setSpecialty(mappedSpecialty);
    const spCard = modeToCard((item as any).specialty_mode);
    setSpecialtyMode(spCard.cardMode); setIsSpecialtyActive(spCard.isActive || mappedSpecialty.length > 0);
    setSelectAllSpecialty((item as any).specialty_all ?? false);

    setIsActive(item.is_active);
    setEditIndex(item.id);

    const hasEntities = !!(
      item.provider?.length > 0 || item.doctor?.length > 0 ||
      item.networks?.length > 0 || item.insuranceplan?.length > 0 ||
      item.policy?.length > 0 || item.patients?.length > 0 ||
      item.vip?.length > 0 || item.payer?.length > 0 ||
      item.user_level?.length > 0 || specialtyData.length > 0 ||
      ((item as any).provider_mode      && (item as any).provider_mode      !== "DISABLED") ||
      ((item as any).network_mode       && (item as any).network_mode       !== "DISABLED") ||
      ((item as any).insuranceplan_mode && (item as any).insuranceplan_mode !== "DISABLED") ||
      ((item as any).payer_mode         && (item as any).payer_mode         !== "DISABLED") ||
      ((item as any).policy_mode        && (item as any).policy_mode        !== "DISABLED") ||
      ((item as any).patient_mode       && (item as any).patient_mode       !== "DISABLED") ||
      ((item as any).vip_mode           && (item as any).vip_mode           !== "DISABLED") ||
      ((item as any).doctor_mode        && (item as any).doctor_mode        !== "DISABLED") ||
      ((item as any).user_mode          && (item as any).user_mode          !== "DISABLED") ||
      ((item as any).specialty_mode     && (item as any).specialty_mode     !== "DISABLED")
    );
    setShowEntities(hasEntities);
  };

  const handleDownload = (item: Datum) => {
    const rows = ["relation_name;activity_type;activity_one;types_main;activity_one_medicalgroups;activity_one_customgroups;activity_types;activity_two;types_not_allowed;activity_two_medicalgroups;activity_two_customgroups;provider;provider_mode;provider_all;networks;network_mode;network_all;insuranceplan;insuranceplan_mode;insuranceplan_all;payer;payer_mode;payer_all;patients;patient_mode;patient_all;policy;policy_mode;policy_all;user_level;user_mode;user_all;doctor;doctor_mode;doctor_all;vip;vip_mode;vip_all;specialty;specialty_mode;specialty_all;is_active"];
    const actCodes = item.main_activity || [];
    const actTypes = item.types_main    || [];
    const notCodes = item.not_allowed_activity || [];
    const notTypes = item.types_not_allowed    || [];
    const medG  = item.main_activity_medicalgroups || [];
    const cusG  = item.main_activity_customgroups  || [];
    const medG2 = item.not_allowed_activity_medicalgroups || [];
    const cusG2 = item.not_allowed_activity_customgroups  || [];
    const prov  = item.provider || [];
    const nets  = item.networks || [];
    const ins   = item.insuranceplan || [];
    const pats  = item.patients || [];
    const pol   = item.policy || [];
    const ul    = item.user_level || [];
    const doc   = item.doctor || [];
    const pay   = item.payer || [];
    const v     = item.vip || [];
    const spec  = (item as any).specialty || [];
    const maxLen = Math.max(actCodes.length, notCodes.length, medG.length, cusG.length, medG2.length, cusG2.length, prov.length, nets.length, ins.length, pats.length, pol.length, ul.length, doc.length, pay.length, v.length, spec.length);
    for (let i = 0; i < Math.max(1, maxLen); i++) {
      rows.push([
        item.relation_name || "",
        i < actTypes.length ? actTypes[i] : "",
        i < actCodes.length ? actCodes[i] : "",
        i < actTypes.length ? actTypes[i] : "",
        i < medG.length  ? medG[i].name  || "" : "",
        i < cusG.length  ? cusG[i].name  || "" : "",
        i < notTypes.length ? notTypes[i] : "",
        i < notCodes.length ? notCodes[i] : "",
        i < notTypes.length ? notTypes[i] : "",
        i < medG2.length ? medG2[i].name || "" : "",
        i < cusG2.length ? cusG2[i].name || "" : "",
        i < prov.length  ? prov[i].Provider_name || "" : "",
        (item as any).provider_mode ?? "DISABLED",
        (item as any).provider_all  ? "true" : "false",
        i < nets.length  ? nets[i].network_name  || "" : "",
        (item as any).network_mode ?? "DISABLED",
        (item as any).network_all  ? "true" : "false",
        i < ins.length   ? ins[i].Plan_name       || "" : "",
        (item as any).insuranceplan_mode ?? "DISABLED",
        (item as any).insuranceplan_all  ? "true" : "false",
        i < pay.length   ? pay[i] : "",
        (item as any).payer_mode ?? "DISABLED",
        (item as any).payer_all  ? "true" : "false",
        i < pats.length  ? pats[i].name || "" : "",
        (item as any).patient_mode ?? "DISABLED",
        (item as any).patient_all  ? "true" : "false",
        i < pol.length   ? pol[i].Policy_name || "" : "",
        (item as any).policy_mode ?? "DISABLED",
        (item as any).policy_all  ? "true" : "false",
        i < ul.length    ? ul[i] : "",
        (item as any).user_mode ?? "DISABLED",
        (item as any).user_all  ? "true" : "false",
        i < doc.length   ? doc[i].Doctor_name || "" : "",
        (item as any).doctor_mode ?? "DISABLED",
        (item as any).doctor_all  ? "true" : "false",
        i < v.length     ? v[i].name || "" : "",
        (item as any).vip_mode ?? "DISABLED",
        (item as any).vip_all  ? "true" : "false",
        i < spec.length  ? spec[i].name || "" : "",
        (item as any).specialty_mode ?? "DISABLED",
        (item as any).specialty_all  ? "true" : "false",
        item.is_active ? "TRUE" : "FALSE",
      ].join(";"));
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `ActivitiesNotAllowedTogether${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Table columns
  // ─────────────────────────────────────────────────────────────────────────────
  const renderActivityCell = (
    codes: string[],
    types: any[],
    fallbackType: string,
    medG: any[],
    cusG: any[],
    onShowAll: (list: Array<{ code: string; type: string }>, title: string) => void,
    title: string,
  ) => {
    const withTypes: Array<{ code: string; type: string }> = (types || []).map((t: any) => ({
      code: t.code?.toString() ?? "",
      type: t.type ?? "CPT",
    })).filter(x => x.code);

    const medGItems = (medG || []).map((g: any) => ({ code: g.name || g, type: "Medical Group" }));
    const cusGItems = (cusG || []).map((g: any) => ({ code: g.name || g, type: "Custom Group" }));
    const allItems  = [...withTypes, ...medGItems, ...cusGItems];

    if (allItems.length === 0) return <div className="w-full text-center text-gray-400">-</div>;

const displayed = allItems.slice(0, 3);
    const hasMore   = allItems.length > 3;

    return (
      <div className="w-full px-2 py-2">
    <div className="flex flex-wrap justify-center gap-1 mb-1">
          {displayed.map((c, idx) => {
            const isGroup = c.type === "Medical Group" || c.type === "Custom Group";
            const col = isGroup
              ? c.type === "Medical Group"
                ? { bg: "bg-green-100", border: "border-green-400", text: "text-green-900" }
                : { bg: "bg-pink-100", border: "border-pink-400", text: "text-pink-900" }
              : getActivityTypeColor(c.type);
            return (
              <div key={idx} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border ${col.bg} ${col.border}`}>
                <span className={`font-semibold text-xs ${col.text}`}>{c.code}</span>
                <span className={`text-[9px] font-bold capitalize ${col.text} opacity-75`}>{c.type}</span>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between pt-1 border-t border-gray-200">
          <span className="text-xs font-bold text-gray-500">{allItems.length}</span>
          {hasMore && (
            <button onClick={() => onShowAll(allItems, title)} className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
              All codes <i className="fa-solid fa-plus text-[9px]"></i>
            </button>
          )}
        </div>
      </div>
    );
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
     cell: ({ row }) => (
  <div className="w-full flex justify-center font-medium">{row.index + 1}</div>
),
    },
    {
      accessorKey: "relation_name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Relation Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const v = row.getValue("relation_name") as string;
        const isDeleted = (row.original as any).is_deleted;
        return (
          <div className={`w-full text-center ${isDeleted ? "line-through text-gray-400" : ""}`}>
            {Array.isArray(v) ? v.join(", ") : v}
          </div>
        );
      },
    },
    {
      accessorKey: "main_activity",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity One{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return renderActivityCell(
          item.main_activity, item.types_main ?? [],
          Array.isArray(item.activity_type) ? item.activity_type[0] : item.activity_type,
          item.main_activity_medicalgroups, item.main_activity_customgroups,
          (list, title) => {
            const scrollY = window.scrollY;
            setAllCodesDialogList(list); setAllCodesDialogTitle(title); setAllCodesDialogOpen(true);
            requestAnimationFrame(() => { window.scrollTo({ top: scrollY, behavior: "instant" as ScrollBehavior }); });
          },
          "Activity One Codes",
        );
      },
    },
    {
      accessorKey: "not_allowed_activity",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center" onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Activity Two{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        return renderActivityCell(
          item.not_allowed_activity, item.types_not_allowed ?? [],
          Array.isArray(item.activity_types) ? item.activity_types[0] : item.activity_types,
          item.not_allowed_activity_medicalgroups, item.not_allowed_activity_customgroups,
          (list, title) => {
            const scrollY = window.scrollY;
            setAllCodesDialogList(list); setAllCodesDialogTitle(title); setAllCodesDialogOpen(true);
            requestAnimationFrame(() => { window.scrollTo({ top: scrollY, behavior: "instant" as ScrollBehavior }); });
          },
          "Activity Two Codes",
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => <Button variant="ghost" className="w-full justify-center">View</Button>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center py-2">
          <button onClick={() => handleOpenDialog(row.original)} className="text-blue-600 hover:text-blue-800" title="View Details">
            <i className="fa-solid fa-eye text-lg"></i>
          </button>
        </div>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-times-circle"></i>
          </span>
          Activities Not Allowed Together
        </h5>
      </div>

      <DataTable2
        columns={columns}
        data={items.result}
        searchColumns={[]}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        validateButtonLabel="Validate"
        addButtonLabel="Add New Relation"
        showAddButton={false}
        onEdit={handleEdit}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) => row.is_deleted === true}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12 }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 18px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add New Relation
              </button>
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Relation Name, Main Activity, Not Allowed Activity, Medical Group, or Custom Group."
              />
              <div style={{ width: 1, height: 28, background: "#cbd5e1" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div style={{ display: "flex", alignItems: "center", padding: 4, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map(option => (
                    <button key={option.value} type="button" onClick={() => setFilterStatus(option.value)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", fontSize: 13, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", transition: "all 0.2s ease", background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent", color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b", boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {option.value === "true" && filterStatus === "true" && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#ffffff" }} />}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* ══ VIEW DIALOG ══════════════════════════════════════════════════════ */}
      <Dialog open={viewDialogOpen} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="max-w-[900px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)", pointerEvents: "none" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-ban" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedItem.relation_name}</h2>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.2)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.25)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize: 9 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0, fontWeight: 400 }}>Rule ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
              </div>

              <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>
                {/* Activity Configuration */}
                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                      <i className="fa-solid fa-code-branch" style={{ fontSize: 12, color: "#3b82f6" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Activity Configuration</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                    {/* Activity One */}
                    {(() => {
                      const tm = selectedItem.types_main || [];
                      const isObj = tm.length > 0 && typeof tm[0] === "object" && tm[0] !== null && "code" in tm[0];
                      const grouped: Record<string, string[]> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
                      const typeColors: Record<string, { bg: string; color: string; border: string; dot: string }> = {
                        CPT:    { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6" },
                        HCPCS:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" },
                        Dental: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff", dot: "#a855f7" },
                        Drugs:  { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", dot: "#f97316" },
                      };
                      if (isObj) {
                        tm.forEach((t: any) => { const k = normaliseType(t.type ?? "CPT") as keyof typeof grouped; if (grouped[k] && t.code) grouped[k].push(t.code.toString()); });
                      } else {
                        const codes = selectedItem.main_activity || [];
                        const actTypes = Array.isArray(selectedItem.activity_type) ? selectedItem.activity_type : Array(codes.length).fill(selectedItem.activity_type || "cpt");
                        codes.forEach((code: string, i: number) => { const k = normaliseType(actTypes[i] || "cpt") as keyof typeof grouped; if (grouped[k]) grouped[k].push(code); });
                      }
                      const hasMed = (selectedItem.main_activity_medicalgroups?.length ?? 0) > 0;
                      const hasCus = (selectedItem.main_activity_customgroups?.length ?? 0) > 0;
                      const hasCodes = Object.values(grouped).some(arr => arr.length > 0);
                      return (
                        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                            <div style={{ width: 6, height: 6, borderRadius: 2, background: "#3b82f6", flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Activity One</span>
                          </div>
                          {hasCodes && (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: hasMed || hasCus ? 10 : 0 }}>
                              {Object.entries(grouped).map(([type, codes]) => {
                                if (!codes.length) return null;
                                const tc = typeColors[type];
                                return (
                                  <div key={type} style={{ background: tc.bg, borderRadius: 10, border: `1px solid ${tc.border}`, padding: "10px 12px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 7 }}>
                                      <span style={{ width: 6, height: 6, borderRadius: 2, background: tc.dot }} />
                                      <span style={{ fontSize: 10, fontWeight: 600, color: tc.color }}>{type}</span>
                                      <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8" }}>{codes.length}</span>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 4 }}>
                                      {codes.map((code, idx) => <span key={idx} style={{ padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#fff", color: tc.color, border: `0.5px solid ${tc.border}` }}>{code}</span>)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {[
                            { show: hasMed, label: "Medical Groups", color: "#22c55e", tagStyle: { background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" }, items: (selectedItem.main_activity_medicalgroups || []).map((g: any) => g.name || g) },
                            { show: hasCus, label: "Custom Groups",  color: "#a855f7", tagStyle: { background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" }, items: (selectedItem.main_activity_customgroups  || []).map((g: any) => g.name || g) },
                          ].map((sec) => sec.show && (
                            <div key={sec.label} style={{ marginTop: 8 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                                <span style={{ width: 6, height: 6, borderRadius: 2, background: sec.color }} />
                                <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b" }}>{sec.label}</span>
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 4 }}>
                                {sec.items.map((item: string, i: number) => <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...sec.tagStyle }}>{item}</span>)}
                              </div>
                            </div>
                          ))}
                          {!hasCodes && !hasMed && !hasCus && <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No activity codes</span>}
                        </div>
                      );
                    })()}

                    {/* Activity Two */}
                    {(() => {
                      const tna = selectedItem.types_not_allowed || [];
                      const isObj = tna.length > 0 && typeof tna[0] === "object" && tna[0] !== null && "code" in tna[0];
                      const grouped: Record<string, string[]> = { CPT: [], HCPCS: [], Dental: [], Drugs: [] };
                      const typeColors: Record<string, { bg: string; color: string; border: string; dot: string }> = {
                        CPT:    { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", dot: "#3b82f6" },
                        HCPCS:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0", dot: "#22c55e" },
                        Dental: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff", dot: "#a855f7" },
                        Drugs:  { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", dot: "#f97316" },
                      };
                      if (isObj) {
                        tna.forEach((t: any) => { const k = normaliseType(t.type ?? "CPT") as keyof typeof grouped; if (grouped[k] && t.code) grouped[k].push(t.code.toString()); });
                      } else {
                        const codes = selectedItem.not_allowed_activity || [];
                        const actTypes = Array.isArray(selectedItem.activity_types) ? selectedItem.activity_types : Array(codes.length).fill(selectedItem.activity_types || "cpt");
                        codes.forEach((code: string, i: number) => { const k = normaliseType(actTypes[i] || "cpt") as keyof typeof grouped; if (grouped[k]) grouped[k].push(code); });
                      }
                      const hasMed = (selectedItem.not_allowed_activity_medicalgroups?.length ?? 0) > 0;
                      const hasCus = (selectedItem.not_allowed_activity_customgroups?.length ?? 0) > 0;
                      const hasCodes = Object.values(grouped).some(arr => arr.length > 0);
                      return (
                        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                            <div style={{ width: 6, height: 6, borderRadius: 2, background: "#a855f7", flexShrink: 0 }} />
                            <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>Activity Two</span>
                          </div>
                          {hasCodes && (
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: hasMed || hasCus ? 10 : 0 }}>
                              {Object.entries(grouped).map(([type, codes]) => {
                                if (!codes.length) return null;
                                const tc = typeColors[type];
                                return (
                                  <div key={type} style={{ background: tc.bg, borderRadius: 10, border: `1px solid ${tc.border}`, padding: "10px 12px" }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 7 }}>
                                      <span style={{ width: 6, height: 6, borderRadius: 2, background: tc.dot }} />
                                      <span style={{ fontSize: 10, fontWeight: 600, color: tc.color }}>{type}</span>
                                      <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8" }}>{codes.length}</span>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 4 }}>
                                      {codes.map((code, idx) => <span key={idx} style={{ padding: "2px 8px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#fff", color: tc.color, border: `0.5px solid ${tc.border}` }}>{code}</span>)}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {[
                            { show: hasMed, label: "Medical Groups", color: "#22c55e", tagStyle: { background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" }, items: (selectedItem.not_allowed_activity_medicalgroups || []).map((g: any) => g.name || g) },
                            { show: hasCus, label: "Custom Groups",  color: "#a855f7", tagStyle: { background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" }, items: (selectedItem.not_allowed_activity_customgroups  || []).map((g: any) => g.name || g) },
                          ].map((sec) => sec.show && (
                            <div key={sec.label} style={{ marginTop: 8 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 6 }}>
                                <span style={{ width: 6, height: 6, borderRadius: 2, background: sec.color }} />
                                <span style={{ fontSize: 10, fontWeight: 600, color: "#64748b" }}>{sec.label}</span>
                              </div>
                              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 4 }}>
                                {sec.items.map((item: string, i: number) => <span key={i} style={{ padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...sec.tagStyle }}>{item}</span>)}
                              </div>
                            </div>
                          ))}
                          {!hasCodes && !hasMed && !hasCus && <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No activity codes</span>}
                        </div>
                      );
                    })()}
                  </div>
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                {/* Target Scope */}
                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                      <i className="fa-solid fa-bullseye" style={{ fontSize: 12, color: "#d97706" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Target Scope & Visibility</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    {[
                      { label: "Providers",       icon: "fa-hospital",          color: "#3b82f6", items: (selectedItem.provider      || []).map((p: any) => p.Provider_name || String(p)), mode: (selectedItem as any).provider_mode,      all: (selectedItem as any).provider_all },
                      { label: "Doctors",         icon: "fa-stethoscope",       color: "#06b6d4", items: (selectedItem.doctor        || []).map((d: any) => d.Doctor_id ? `(${d.Doctor_id}) ${d.Doctor_name}` : d.Doctor_name   || String(d)), mode: (selectedItem as any).doctor_mode,        all: (selectedItem as any).doctor_all },
                      { label: "Networks",        icon: "fa-network-wired",     color: "#8b5cf6", items: (selectedItem.networks ?? (selectedItem as any).network ?? []).map((n: any) => n.network_name || String(n)), mode: (selectedItem as any).network_mode,       all: (selectedItem as any).network_all },
                      { label: "Insurance Plans", icon: "fa-shield-halved",     color: "#f59e0b", items: (selectedItem.insuranceplan || []).map((i: any) => i.Plan_name     || String(i)), mode: (selectedItem as any).insuranceplan_mode, all: (selectedItem as any).insuranceplan_all },
                      { label: "Policies",        icon: "fa-file-lines",        color: "#10b981",
                        items: (selectedItem.policy || []).map((p: any) => {
                          const planNames = (Array.isArray(p.plans) ? p.plans : []).map((pl: any) => pl?.Plan_name || pl?.plan_name || pl?.name || "").filter(Boolean).join(", ");
                          return `${p.Policy_id || ""} - ${p.Policy_name || ""}${planNames ? ` (${planNames})` : ""}`;
                        }),
                        mode: (selectedItem as any).policy_mode, all: (selectedItem as any).policy_all },
                      { label: "Patients",    icon: "fa-users",            color: "#ec4899", items: (selectedItem.patients ?? (selectedItem as any).patient ?? []).map((p: any) => p.name || String(p)), mode: (selectedItem as any).patient_mode,   all: (selectedItem as any).patient_all },
                      { label: "VIP",         icon: "fa-star",             color: "#eab308", items: (selectedItem.vip        || []).map((v: any) => v.name    || String(v)), mode: (selectedItem as any).vip_mode,       all: (selectedItem as any).vip_all },
                      { label: "Specialty",   icon: "fa-briefcase-medical",color: "#0d9488", items: ((selectedItem as any).specialty || []).map((s: any) => s.name || String(s)), mode: (selectedItem as any).specialty_mode, all: (selectedItem as any).specialty_all },
                      { label: "Payer",       icon: "fa-building-columns", color: "#6366f1", items: (selectedItem.payer      || []).map((p: any) => String(p)), mode: (selectedItem as any).payer_mode,     all: (selectedItem as any).payer_all },
                      { label: "User Levels", icon: "fa-user-gear",        color: "#64748b", items: (selectedItem.user_level || []).map((u: any) => String(u)), mode: (selectedItem as any).user_mode,      all: (selectedItem as any).user_all },
                    ].map((e) => {
                      const isExclude  = e.mode === "EXCLUDED";
                      const isDisabled = !e.mode || e.mode === "DISABLED";
                      return (
                        <div key={e.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", display: "flex", flexDirection: "column" as const }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                              <div style={{ width: 24, height: 24, borderRadius: 6, background: `${e.color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <i className={`fa-solid ${e.icon}`} style={{ fontSize: 10, color: e.color }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>{e.label}</span>
                            </div>
                            {!isDisabled && (
                              <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 9999, fontWeight: 600, background: isExclude ? "#fef2f2" : "#f0fdf4", color: isExclude ? "#dc2626" : "#15803d", border: `1px solid ${isExclude ? "#fecaca" : "#bbf7d0"}` }}>
                                {isExclude ? "Exclusion" : "Inclusion"}
                              </span>
                            )}
                          </div>
                          {e.all
                            ? <span style={{ fontSize: 11, color: "#475569", fontStyle: "italic" }}>All {e.label}</span>
                            : e.items.length > 0
                              ? <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                                  {e.items.map((item: string, i: number) => <span key={i} style={{ fontSize: 11, padding: "3px 10px", borderRadius: 9999, background: "#f1f5f9", color: "#334155", border: "1px solid #e2e8f0", fontWeight: 500 }}>{item}</span>)}
                                </div>
                              : <span style={{ color: "#cbd5e1", fontSize: 12, fontStyle: "italic" }}>No items</span>
                          }
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                {/* Audit Trail */}
                <div style={{ padding: "20px 28px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize" as const, letterSpacing: "0.05em" }}>Created By</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize" as const, letterSpacing: "0.05em" }}>Created On</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).createdat ? new Date((selectedItem as any).createdat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </span>
                      </div>
                    </div>
                <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "capitalize" as const, letterSpacing: "0.05em" }}>Last Updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                          {(selectedItem as any).updatedat ? new Date((selectedItem as any).updatedat).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                 {Array.isArray(selectedItem.updated_by) && selectedItem.updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)", marginTop: 12 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {selectedItem.updated_by.length} update{selectedItem.updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {(selectedItem.updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>
            {entry.at ? new Date(entry.at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
        </div>
      ))}
      {selectedItem.updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button onClick={() => setShowAllUpdates(v => !v)} style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}>
            {showAllUpdates ? "Show less ↑" : `+${selectedItem.updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
                  </div>
          

              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>
                  Close Details
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      

    {/* ══ ADD / EDIT DIALOG ════════════════════════════════════════════════ */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            const scrollY = window.scrollY;
            setIsDialogOpen(false);
            requestAnimationFrame(() => { window.scrollTo({ top: scrollY, behavior: "instant" as ScrollBehavior }); });
          } else {
            setIsDialogOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-6xl h-[90vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Activities Not Allowed Together" : "Edit Activities Not Allowed Together"}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Fill in the details below to {editIndex === null ? "create" : "update"} Activities Not Allowed Together.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
            <SectionCard num={1} title="Basic information" subtitle="Name and status of the relation">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <Label htmlFor="relation_name">Relation Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input id="relation_name" value={relationName} onChange={e => setRelationName(e.target.value)} placeholder="Enter Relation Name" className="mt-1" />
                </div>
                <div className="w-48 flex-shrink-0">
                  <Label>Status</Label>
                  <div className="flex gap-2 mt-1">
                    <button type="button" onClick={() => setIsActive(true)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${isActive ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Active</button>
                    <button type="button" onClick={() => setIsActive(false)} className={`flex-1 h-10 rounded-md text-sm font-medium border transition-all ${!isActive ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>Inactive</button>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="Activity configuration" subtitle="Choose one source for each side">
              <div className="space-y-5">
                {/* Activity One */}
                <SourcePicker
                  title="Activity One"
                  selected={selectedMainOption}
                  onSelect={setSelectedMainOption}
                  options={[
                    { key: "list_of_Activity_code", label: "Activity Codes", icon: "fa-code", count: mainActivitiesByType.CPT.length + mainActivitiesByType.HCPCS.length + mainActivitiesByType.Dental.length + mainActivitiesByType.Drugs.length },
                    { key: "medical_Activity_groups", label: "Medical Groups", icon: "fa-layer-group", count: mainActivityMedicalGroups.length },
                    { key: "custom_Activity_groups", label: "Custom Groups", icon: "fa-cubes", count: mainActivityCustomGroups.length },
                  ]}
                >
                  {selectedMainOption === "list_of_Activity_code" && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><i className="fa-solid fa-code text-blue-600" /> Activity One Codes by Type</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => (
                          <div key={key} className="bg-white p-3 rounded-lg border border-gray-200">
                            <Label className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-bold border">{key}</span>
                              <span className="text-xs text-gray-500">({mainActivitiesByType[key].length} codes)</span>
                            </Label>
                            <ListofActivityCode onChange={(selected: Option[]) => setMainActivitiesByType(prev => ({ ...prev, [key]: selected }))} type={key} defaultValue={mainActivitiesByType[key]} clear={false} isValid={true} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedMainOption === "medical_Activity_groups" && (
                    <ListOfActivityMedical onChange={(opts: Option[]) => setMainActivityMedicalGroups(opts)} defaultValue={mainActivityMedicalGroups} clear={clearMainActivityMedicalGroups} />
                  )}
                  {selectedMainOption === "custom_Activity_groups" && (
                    <ListOfActivityCustom onChange={(opts: Option[]) => setMainActivityCustomGroups(opts)} defaultValue={mainActivityCustomGroups} clear={clearMainActivityCustomGroups} />
                  )}
                </SourcePicker>

                {/* Activity Two */}
                <SourcePicker
                  title="Activity Two"
                  selected={selectedNotAllowedOption}
                  onSelect={setSelectedNotAllowedOption}
                  options={[
                    { key: "list_of_Activity", label: "Activity Codes", icon: "fa-code", count: notAllowedActivitiesByType.CPT.length + notAllowedActivitiesByType.HCPCS.length + notAllowedActivitiesByType.Dental.length + notAllowedActivitiesByType.Drugs.length },
                    { key: "medical_Activity_groups", label: "Medical Groups", icon: "fa-layer-group", count: NotallowedActivityMedicalGroups.length },
                    { key: "custom_Activity_groups", label: "Custom Groups", icon: "fa-cubes", count: NotallowedActivityCustomGroups.length },
                  ]}
                >
                  {selectedNotAllowedOption === "list_of_Activity" && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2"><i className="fa-solid fa-code text-purple-600" /> Activity Two Codes by Type</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {(["CPT", "HCPCS", "Dental", "Drugs"] as const).map((key) => (
                          <div key={key} className="bg-white p-3 rounded-lg border border-gray-200">
                            <Label className="flex items-center gap-2 mb-2">
                              <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-bold border">{key}</span>
                              <span className="text-xs text-gray-500">({notAllowedActivitiesByType[key].length} codes)</span>
                            </Label>
                            <ListofActivityCode onChange={(selected: Option[]) => setNotAllowedActivitiesByType(prev => ({ ...prev, [key]: selected }))} type={key} defaultValue={notAllowedActivitiesByType[key]} clear={false} isValid={true} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedNotAllowedOption === "medical_Activity_groups" && (
                    <ListOfActivityMedical onChange={(opts: Option[]) => setNotAllowedActivityMedicalGroups(opts)} defaultValue={NotallowedActivityMedicalGroups} clear={clearNotAllowedActivityMedicalGroups} />
                  )}
                  {selectedNotAllowedOption === "custom_Activity_groups" && (
                    <ListOfActivityCustom onChange={(opts: Option[]) => setNotAllowedActivityCustomGroups(opts)} defaultValue={NotallowedActivityCustomGroups} clear={clearNotAllowedActivityCustomGroups} />
                  )}
                </SourcePicker>
              </div>
            </SectionCard>

            {/* Section 3 – Entities */}
            <div className="border-2 border-blue-200 rounded-xl overflow-hidden mb-4 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">3</span>
                  <div>
                    <p className="text-sm font-semibold text-blue-900">Entities Configuration</p>
                    <p className="text-xs text-blue-600">Scope and visibility — who this rule applies to</p>
                  </div>
                </div>
                <button type="button" onClick={handleToggleEntities} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${showEntities ? "bg-green-500" : "bg-gray-300"}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showEntities ? "translate-x-6" : "translate-x-1"}`} />
                </button>
              </div>
              {showEntities && (
                <div className="p-5 bg-blue-50">
                
                  <div className="grid grid-cols-2 gap-3">
                    <EntityCard label="Providers" icon="fa-hospital" color="blue" isActive={isProviderActive} mode={providerMode}
                      onExclusion={() => { if (providerMode === "EXCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("EXCLUSION"); setIsProviderActive(true); } }}
                      onInclusion={() => { if (providerMode === "INCLUSION") { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode(null); setIsProviderActive(false); } else { setProvider([]); setClearProvider(true); setSelectAllProviders(false); setProviderMode("INCLUSION"); setIsProviderActive(true); } }}
                      onClear={() => { setProvider([]); setProviderMode(null); setIsProviderActive(false); setClearProvider(true); setSelectAllProviders(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllProviders} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllProviders(c); if (c) { setProvider([]); setClearProvider(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-blue-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Providers</span>
                      </label>
                      {!selectAllProviders && <ListOfProviderName onChange={(opts: Option[]) => setProvider(opts)} defaultValue={provider} clear={clearProvider} />}
                    </EntityCard>

                    <EntityCard label="Doctor" icon="fa-stethoscope" color="cyan" isActive={isDoctorActive} mode={doctorMode}
                      onExclusion={() => { if (doctorMode === "EXCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("EXCLUSION"); setIsDoctorActive(true); } }}
                      onInclusion={() => { if (doctorMode === "INCLUSION") { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode(null); setIsDoctorActive(false); } else { setDoctor([]); setClearDoctor(true); setSelectAllDoctor(false); setDoctorMode("INCLUSION"); setIsDoctorActive(true); } }}
                      onClear={() => { setDoctor([]); setDoctorMode(null); setIsDoctorActive(false); setClearDoctor(true); setSelectAllDoctor(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllDoctor} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllDoctor(c); if (c) { setDoctor([]); setClearDoctor(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-cyan-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Doctors</span>
                      </label>
                      {!selectAllDoctor && <ListOfDoctorName onChange={(opts: Option[]) => setDoctor(opts)} defaultValue={doctor} clear={clearDoctor} />}
                    </EntityCard>

                    <EntityCard label="Networks" icon="fa-network-wired" color="purple" isActive={isNetworkActive} mode={networkMode}
                      onExclusion={() => { if (networkMode === "EXCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("EXCLUSION"); setIsNetworkActive(true); } }}
                      onInclusion={() => { if (networkMode === "INCLUSION") { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode(null); setIsNetworkActive(false); } else { setNetworks([]); setClearNetworks(true); setSelectAllNetworks(false); setNetworkMode("INCLUSION"); setIsNetworkActive(true); } }}
                      onClear={() => { setNetworks([]); setNetworkMode(null); setIsNetworkActive(false); setClearNetworks(true); setSelectAllNetworks(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllNetworks} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllNetworks(c); if (c) { setNetworks([]); setClearNetworks(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-purple-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Networks</span>
                      </label>
                      {!selectAllNetworks && <ListOfNewNetworkName onChange={(opts: Option[]) => setNetworks(opts)} defaultValue={networks} clear={clearNetworks} />}
                    </EntityCard>

                    <EntityCard label="Insurance Plan" icon="fa-shield-halved" color="amber" isActive={isInsuranceActive} mode={insuranceMode}
                      onExclusion={() => { if (insuranceMode === "EXCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("EXCLUSION"); setIsInsuranceActive(true); } }}
                      onInclusion={() => { if (insuranceMode === "INCLUSION") { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode(null); setIsInsuranceActive(false); } else { setInsuranceplan([]); setClearInsuranceplan(true); setSelectAllInsurance(false); setInsuranceMode("INCLUSION"); setIsInsuranceActive(true); } }}
                      onClear={() => { setInsuranceplan([]); setInsuranceMode(null); setIsInsuranceActive(false); setClearInsuranceplan(true); setSelectAllInsurance(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllInsurance} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllInsurance(c); if (c) { setInsuranceplan([]); setClearInsuranceplan(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-amber-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Insurance Plans</span>
                      </label>
                      {!selectAllInsurance && <ListOfInsuranceplanName onChange={(opts: Option[]) => setInsuranceplan(opts)} defaultValue={insuranceplan} clear={clearInsuranceplan} />}
                    </EntityCard>

                    <EntityCard label="Policy" icon="fa-file-lines" color="emerald" isActive={isPolicyActive} mode={policyMode}
                      onExclusion={() => { if (policyMode === "EXCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("EXCLUSION"); setIsPolicyActive(true); } }}
                      onInclusion={() => { if (policyMode === "INCLUSION") { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode(null); setIsPolicyActive(false); } else { setPolicy([]); setClearPolicy(true); setSelectAllPolicy(false); setPolicyMode("INCLUSION"); setIsPolicyActive(true); } }}
                      onClear={() => { setPolicy([]); setPolicyMode(null); setIsPolicyActive(false); setClearPolicy(true); setSelectAllPolicy(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPolicy} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPolicy(c); if (c) { setPolicy([]); setClearPolicy(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-emerald-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Policies</span>
                      </label>
                      {!selectAllPolicy && <PolicyNames onChange={(opts: PolicyItem[]) => setPolicy(opts)} defaultValue={policy} clear={clearPolicy} />}
                    </EntityCard>

                    <EntityCard label="Patients" icon="fa-users" color="pink" isActive={isPatientActive} mode={patientMode}
                      onExclusion={() => { if (patientMode === "EXCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("EXCLUSION"); setIsPatientActive(true); } }}
                      onInclusion={() => { if (patientMode === "INCLUSION") { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode(null); setIsPatientActive(false); } else { setPatients([]); setClearPatient(true); setSelectAllPatients(false); setPatientMode("INCLUSION"); setIsPatientActive(true); } }}
                      onClear={() => { setPatients([]); setPatientMode(null); setIsPatientActive(false); setClearPatient(true); setSelectAllPatients(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPatients} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPatients(c); if (c) { setPatients([]); setClearPatient(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-pink-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Patients</span>
                      </label>
                      {!selectAllPatients && <VipPatientSelector onChange={(opts: Option[]) => setPatients(opts)} defaultValue={patients} clear={clearPatient} />}
                    </EntityCard>

                    <EntityCard label="VIP" icon="fa-star" color="yellow" isActive={isVipActive} mode={vipMode}
                      onExclusion={() => { if (vipMode === "EXCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("EXCLUSION"); setIsVipActive(true); } }}
                      onInclusion={() => { if (vipMode === "INCLUSION") { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode(null); setIsVipActive(false); } else { setVip([]); setClearVip(true); setSelectAllVip(false); setVipMode("INCLUSION"); setIsVipActive(true); } }}
                      onClear={() => { setVip([]); setVipMode(null); setIsVipActive(false); setClearVip(true); setSelectAllVip(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllVip} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllVip(c); if (c) { setVip([]); setClearVip(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-yellow-600" />
                        <span className="text-sm font-medium text-slate-600">Select All VIP</span>
                      </label>
                      {!selectAllVip && <Vip onChange={(opts: Option[]) => setVip(opts)} defaultValue={vip} clear={clearVip} />}
                    </EntityCard>

                    <EntityCard label="Specialty" icon="fa-briefcase-medical" color="teal" isActive={isSpecialtyActive} mode={specialtyMode}
                      onExclusion={() => { if (specialtyMode === "EXCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("EXCLUSION"); setIsSpecialtyActive(true); } }}
                      onInclusion={() => { if (specialtyMode === "INCLUSION") { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode(null); setIsSpecialtyActive(false); } else { setSpecialty([]); setClearSpecialty(true); setSelectAllSpecialty(false); setSpecialtyMode("INCLUSION"); setIsSpecialtyActive(true); } }}
                      onClear={() => { setSpecialty([]); setSpecialtyMode(null); setIsSpecialtyActive(false); setClearSpecialty(true); setSelectAllSpecialty(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllSpecialty} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllSpecialty(c); if (c) { setSpecialty([]); setClearSpecialty(true); } }); }} className="w-4 h-4 rounded border-slate-300 accent-teal-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Specialties</span>
                      </label>
                      {!selectAllSpecialty && <ListOfSpecialty onChange={(opts: Option[]) => setSpecialty(opts)} defaultValue={specialty} clear={clearSpecialty} />}
                    </EntityCard>

                    <EntityCard label="Payer" icon="fa-building-columns" color="indigo" isActive={isPayerActive} mode={payerMode}
                      onExclusion={() => { if (payerMode === "EXCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("EXCLUSION"); setIsPayerActive(true); } }}
                      onInclusion={() => { if (payerMode === "INCLUSION") { setPayer([]); setSelectAllPayer(false); setPayerMode(null); setIsPayerActive(false); } else { setPayer([]); setSelectAllPayer(false); setPayerMode("INCLUSION"); setIsPayerActive(true); } }}
                      onClear={() => { setPayer([]); setPayerMode(null); setIsPayerActive(false); setSelectAllPayer(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllPayer} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllPayer(c); if (c) setPayer([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-indigo-600" />
                        <span className="text-sm font-medium text-slate-600">Select All Payers</span>
                      </label>
                      {!selectAllPayer && <Input value={Payer.join(",")} onChange={e => setPayer([e.target.value])} placeholder="Enter Payer" />}
                    </EntityCard>

                    <EntityCard label="User Level" icon="fa-user-gear" color="slate" isActive={isUserActive} mode={userMode}
                      onExclusion={() => { if (userMode === "EXCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("EXCLUSION"); setIsUserActive(true); } }}
                      onInclusion={() => { if (userMode === "INCLUSION") { setUserLevel([]); setSelectAllUser(false); setUserMode(null); setIsUserActive(false); } else { setUserLevel([]); setSelectAllUser(false); setUserMode("INCLUSION"); setIsUserActive(true); } }}
                      onClear={() => { setUserLevel([]); setUserMode(null); setIsUserActive(false); setSelectAllUser(false); }}>
                      <label className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded-md hover:bg-slate-50 transition-colors">
                        <input type="checkbox" checked={selectAllUser} onChange={(e) => { const c = e.target.checked; preserveScrollCheckbox(e, () => { setSelectAllUser(c); if (c) setUserLevel([]); }); }} className="w-4 h-4 rounded border-slate-300 accent-slate-600" />
                        <span className="text-sm font-medium text-slate-600">Select All User Levels</span>
                      </label>
                      {!selectAllUser && <Input value={userLevel.join(",")} onChange={e => setUserLevel([e.target.value])} placeholder="Enter User Level" />}
                    </EntityCard>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => editIndex === null ? addItem() : updateItem(editIndex)} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Relation " : "Update Relation "}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ ALL CODES DIALOG ═════════════════════════════════════════════════ */}
      <Dialog
        open={allCodesDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            const scrollY = window.scrollY;
            setAllCodesDialogOpen(false);
            requestAnimationFrame(() => { window.scrollTo({ top: scrollY, behavior: "instant" as ScrollBehavior }); });
          } else {
            setAllCodesDialogOpen(true);
          }
        }}
      >
        <DialogContent className="max-w-5xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list"></i> {allCodesDialogTitle}
              <span className="ml-auto text-sm font-normal text-gray-600">({allCodesDialogList.length} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 overflow-y-auto max-h-[calc(85vh-140px)] pr-2">
            <div className="grid grid-cols-4 gap-3">
              {allCodesDialogList.map((item, index) => {
                const col = getActivityTypeColor(item.type);
                return (
                  <div key={index} className={`flex items-center justify-between gap-2 p-3 bg-white rounded-lg border hover:shadow-md transition-all ${col.border}`}>
                    <span className={`font-bold text-base ${col.text}`}>{item.code}</span>
                    <div className={`w-px h-6 ${col.border}`}></div>
                    <span className={`text-xs font-bold capitalize ${col.text} opacity-75`}>{item.type}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end pt-4 border-t mt-4">
            <Button onClick={() => setAllCodesDialogOpen(false)} className="bg-blue-600 hover:bg-blue-700">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ══ FORCE / HARD DELETE DIALOG ═══════════════════════════════════════ */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>Permanent Delete</h3>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0, lineHeight: 1.6 }}>
                    This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> this relation.<br />This action <strong>cannot be undone</strong>.
                  </p>
                  {forceError && (
                    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button
                      onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                      Cancel
                    </button>
                    <button
                      onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
                      style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {/* Soft Delete / Status */}
              {(forceAction === "softDelete" || forceAction === "status") && (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                    {forceAction === "status" ? "Status Change Blocked" : "Relation Is In Use"}
                  </h3>
                  <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
                      <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
                    </div>
                  </div>
                  <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                    Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
                  </p>
                  <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
                    <button onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
                      style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Cancel</button>
                    <button onClick={() => {
                        if (!forceId) return;
                        if (forceAction === "softDelete") forceSoftDelete(forceId);
                        else if (forceAction === "status") {
                          const item = items.result.find(i => i.id === forceId);
                          if (item) toggleActiveStatus(item, true);
                        }
                      }}
                      style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
                      <i className="fa-solid fa-bolt mr-2"></i>Force {forceAction === "status" ? "Update" : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default ActivityNotAllowed;
